import { motion } from 'framer-motion';
import { Wrench, Calendar, Clock, Hash, Truck } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';

export function EquipmentSpotlightSection() {
  const proposal = useProposalData();

  // Only show for service proposals with equipment spotlight data
  if (proposal.proposalType !== 'service' || !proposal.equipmentSpotlight) {
    return null;
  }

  const equipment = proposal.equipmentSpotlight;

  return (
    <Section id="equipment-spotlight" className="bg-gray-50" pattern="diagonal">
      <SectionHeader
        title="EQUIPMENT FOR SERVICE"
        subtitle="Unit Details"
      />

      <motion.div
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="grid lg:grid-cols-2 gap-0">
          {/* Equipment Image */}
          <div className="relative aspect-[4/3] lg:aspect-auto bg-hul-navy/5">
            {equipment.image ? (
              <img
                src={equipment.image}
                alt={`${equipment.manufacturer} ${equipment.model}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <Truck className="w-24 h-24 text-hul-navy/20 mx-auto mb-4" />
                  <p className="text-hul-navy/40 font-heading uppercase tracking-wider">
                    Equipment Image
                  </p>
                </div>
              </div>
            )}

            {/* Service Badge Overlay */}
            <div className="absolute top-4 left-4">
              <motion.div
                className="flex items-center gap-2 px-4 py-2 bg-hul-red text-white rounded-full shadow-lg"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <Wrench className="w-4 h-4" />
                <span className="font-heading uppercase tracking-wider text-sm">
                  Service Estimate
                </span>
              </motion.div>
            </div>
          </div>

          {/* Equipment Details */}
          <div className="p-8 lg:p-12">
            {/* Manufacturer & Model */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-hul-red font-mono text-sm uppercase tracking-wider mb-2">
                {equipment.manufacturer}
              </div>
              <h3 className="font-display text-4xl lg:text-5xl text-hul-navy tracking-wide mb-6">
                {equipment.model}
              </h3>
            </motion.div>

            {/* Specs Grid */}
            <motion.div
              className="grid grid-cols-2 gap-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {equipment.serial && (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-hul-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Hash className="w-5 h-5 text-hul-navy" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                      Serial Number
                    </div>
                    <div className="font-mono text-hul-navy font-medium">
                      {equipment.serial}
                    </div>
                  </div>
                </div>
              )}

              {equipment.fleetNo && (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-hul-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Truck className="w-5 h-5 text-hul-navy" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                      Fleet Number
                    </div>
                    <div className="font-mono text-hul-navy font-medium">
                      {equipment.fleetNo}
                    </div>
                  </div>
                </div>
              )}

              {equipment.year && (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-hul-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-hul-navy" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                      Year
                    </div>
                    <div className="font-mono text-hul-navy font-medium">
                      {equipment.year}
                    </div>
                  </div>
                </div>
              )}

              {equipment.hours !== undefined && (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-hul-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-hul-navy" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                      Hours
                    </div>
                    <div className="font-mono text-hul-navy font-medium">
                      {equipment.hours.toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Decorative Element */}
            <motion.div
              className="mt-8 pt-8 border-t border-gray-100"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-gray-500 text-sm">
                Review the service tasks below and select which repairs you'd like to approve.
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </Section>
  );
}
